import React, { memo, useCallback } from "react";
import IconMC from "./IconMC";

/**
 * @fileoverview Componente TagMC (Atom) optimizado para mostrar un tag con opción de eliminar.
 * @module TagMC
 * @category Component - Atom
 * @since 1.0.0
 * @version 2.0.0 - Memoización, accesibilidad y performance
 */

/**
 * Componente atómico para mostrar un tag con botón de eliminar accesible.
 *
 * @param {Object} props
 * @param {string} props.label - Texto del tag
 * @param {function} props.onClick - Handler para eliminar el tag
 * @param {string} [props.className] - Clases adicionales
 * @returns {JSX.Element}
 *
 * @example
 * <TagMC label="VIP" onClick={handleRemove} />
 */
const TagMC = memo(({ label, onClick, className = "" }) => {
  // Handler memoizado para accesibilidad y performance
  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") onClick?.(e);
    },
    [onClick]
  );

  return (
    <div
      className={`tw-flex tw-items-center tw-text-gray-500 tw-border tw-border-gray-200 tw-bg-white tw-px-2 tw-py-1 tw-rounded-[5px] tw-gap-1 ${className}`}
      tabIndex={-1}
      aria-label={label}
      role="listitem"
    >
      <span className="tw-text-xs tw-whitespace-nowrap tw-truncate" title={label}>{label}</span>
      <span
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyPress={handleKeyPress}
        className="tw-cursor-pointer tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
        aria-label="Eliminar tag"
      >
        <IconMC name="close-outline" size="base" />
      </span>
    </div>
  );
});

TagMC.displayName = "TagMC";

export default TagMC;
